import { TextRun } from "docx";
import convertToPlainTextCustom from '@cclib/functions/quill/convertToPlainTextCustom.js';
import * as m from "@app/paraglide/messages.js";

/**
 * Converts HTML formatted text to an array of TextRun objects with proper formatting.
 * Preserves bold, italic, and underline formatting from HTML tags.
 * Optionally prepends speaker and/or recipient labels.
 * 
 * @param {string} html - HTML formatted text
 * @param {string} [speakerNames] - Comma-separated speaker names to prepend
 * @param {string} [recipientNames] - Comma-separated recipient names to prepend
 * @returns {Array<TextRun>} Array of TextRun objects with proper formatting
 */
export default function convertToFormattedTextRuns(html, speakerNames, recipientNames) {
    if (!html) return [new TextRun({ text: "" })];
    
    // Convert <br> tags to newline characters first
    const htmlWithLineBreaks = html.replace(/<br\s*\/?>/gi, '\n');
    
    // Then remove any HTML that isn't related to formatting
    let plainText = convertToPlainTextCustom(htmlWithLineBreaks);
    if (!plainText || plainText === "<br>") return [new TextRun({ text: "" })];

    if (speakerNames && recipientNames) {
        plainText = m.ccsl_export_speaker_to_recipient({
            speakers: speakerNames,
            recipients: recipientNames
        }) + "\n" + plainText;
    } else if (speakerNames) {
        plainText = m.ccsl_export_speaker_only({ speakers: speakerNames }) + "\n" + plainText;
    } else if (recipientNames) {
        plainText = m.ccsl_export_recipient_only({ recipients: recipientNames }) + "\n" + plainText;
    }
    
    // Now parse the formatting tags
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${plainText}</div>`, 'text/html');
    const textRuns = [];
    
    // Recursive function to process nodes and their formatting
    function processNode(node, formatting = { bold: false, italic: false, underline: false }) {
        if (node.nodeType === Node.TEXT_NODE) {
            const lines = node.textContent.split('\n');
            
            lines.forEach((line, index) => {
                textRuns.push(new TextRun({
                    text: line,
                    bold: formatting.bold,
                    italics: formatting.italic,
                    underline: formatting.underline
                }));
                
                if (index < lines.length - 1) {
                    textRuns.push(new TextRun({ break: 1 }));
                }
            });
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            const newFormatting = { ...formatting };
            
            if (node.tagName === 'STRONG' || node.tagName === 'B') {
                newFormatting.bold = true;
            } else if (node.tagName === 'EM' || node.tagName === 'I') {
                newFormatting.italic = true;
            } else if (node.tagName === 'U') {
                newFormatting.underline = true;
            } else if (node.tagName === 'BR') {
                textRuns.push(new TextRun({ break: 1 }));
            }
            
            Array.from(node.childNodes).forEach(child => {
                processNode(child, newFormatting);
            });
        }
    }
    
    Array.from(doc.body.firstChild.childNodes).forEach(node => {
        processNode(node);
    });
    
    return textRuns.length > 0 ? textRuns : [new TextRun({ text: plainText })];
}
